<script setup lang="ts">
import { FormKitLazyProvider } from '@formkit/vue'
import type {
  PlpGridType,
  PlpGridItem,
  PlpGridItemWithType,
  PlpFunctionalBarProps,
} from './PlpFunctionalBar.props'

import type { Breakpoints } from '@design-system/configs/tailwind/screens'
import { SIDEBAR_IDS } from '@design-system/data/sidebarIds'

const props = defineProps<PlpFunctionalBarProps>()
const { gridsMap } = props
const sidePlpFilters = SIDEBAR_IDS.plpFiltersAside
const cookieLifetime = 604800 // Cookie duration of 7 days in seconds

const { breakpoint } = useProjectBreakpoint()

const { currentWebsite } = useWebsite()
const { openDialog } = useDialog()
const { ts } = useI18n()

const {
  transformRefinements,
  getActiveRefinementLength,
  getAllActiveRefinementLength,
} = await useTemplatePlp()

const getCookieKeyForWebsite = (website: string) =>
  `${website}-grid-layout-cookies`

const getCookiesLayout = (website: string) => {
  return useCookie<{ name: PlpGridType | undefined }>(
    getCookieKeyForWebsite(website),
    { maxAge: cookieLifetime }
  )
}

const currentWebsiteValue = currentWebsite.value
const cookiesLayout = getCookiesLayout(currentWebsiteValue)

const activeBreakpoint = breakpoint.active()

const isKeyOf = <O extends object>(
  key: string,
  object: O
): key is string & keyof O => key in object

const getIcon = (
  gridItem: PlpGridItem<PlpGridType>,
  activeBreakpoint: Breakpoints
) => {
  let iconBreakpoint: Breakpoints = 'sm'

  if (isKeyOf(activeBreakpoint, gridItem.icon)) {
    iconBreakpoint = activeBreakpoint
  } else if (
    breakpoint.isGreaterOrEqual('lg') &&
    isKeyOf('lg', gridItem.icon)
  ) {
    iconBreakpoint = 'lg'
  } else if (
    breakpoint.isGreaterOrEqual('md') &&
    isKeyOf('md', gridItem.icon)
  ) {
    iconBreakpoint = 'md'
  }

  return gridItem.icon[iconBreakpoint]
}

const activeIcon = ref<PlpGridType>()
const isIconsMobile = ref(false)
const changePosition = <T,>(arr: T[], from: number, to: number): void => {
  const [element] = arr.splice(from, 1)
  arr.splice(to, 0, element)
}

const radioOptions = ref<{ label: string; value: PlpGridType }[]>([
  {
    label: ts('plp.filters.1Col'),
    value: 'grid321',
  },
  {
    label: ts('plp.filters.2Cols'),
    value: 'grid432',
  },
  { label: ts('plp.filters.wall'), value: 'gridWall' },
])
if (currentWebsiteValue === 'giorgio-armani') {
  const updatedOptions = radioOptions.value.map((option, index) => {
    if (index === 0) {
      return { ...option, label: ts('plp.filters.2Cols') }
    } else if (index === 1) {
      return { ...option, label: ts('plp.filters.1Col') }
    }
    return option
  })
  radioOptions.value = updatedOptions
  changePosition(radioOptions.value, 0, 1)
}

const emit = defineEmits<{
  (e: 'grid-layout', value: PlpGridItemWithType): void
  (e: 'open-filter', value: string): void
}>()
const toggleMobileSelector = () => {
  if (activeBreakpoint?.value === 'sm' || activeBreakpoint?.value === '') {
    isIconsMobile.value = !isIconsMobile.value
  }
}
const setGrid = (layout: keyof typeof gridsMap) => {
  const selectedGrid = gridsMap[layout]
  if (activeIcon.value !== selectedGrid.name) {
    activeIcon.value = selectedGrid.name
  }
}

const openFilters = (filter: string) => {
  emit('open-filter', filter)
}
const getFirstAvailableGrid = (): PlpGridType => {
  return (
    (Object.keys(gridsMap).find(
      key => key !== 'gridMoodboard' || props.editorialContent?.length
    ) as PlpGridType) || 'grid432'
  )
}
const defaultGridLayout =
  currentWebsiteValue === 'giorgio-armani' ? 'grid321' : 'grid432'
const storedLayout = cookiesLayout.value?.name
const hasEditorialContent = props.editorialContent?.length > 0

const getInitialLayout = () => {
  if (storedLayout) {
    return storedLayout === 'gridMoodboard' && !hasEditorialContent
      ? getFirstAvailableGrid()
      : storedLayout
  } else {
    return hasEditorialContent ? 'gridMoodboard' : defaultGridLayout
  }
}

watch(
  activeIcon,
  (newVal, oldVal) => {
    if (!newVal || newVal === oldVal) return
    const selectedGrid = gridsMap[newVal]
    cookiesLayout.value = { name: newVal }
    emit('grid-layout', selectedGrid)

    if (
      oldVal !== newVal &&
      oldVal !== undefined &&
      (activeBreakpoint.value === 'sm' || activeBreakpoint.value === '')
    ) {
      toggleMobileSelector()
    }
  },
  { immediate: true, flush: 'post' }
)

const initialLayout = getInitialLayout()
if (initialLayout) {
  setGrid(initialLayout)
}

if (hasEditorialContent) {
  radioOptions.value.unshift({
    label: ts('plp.filters.editorial'),
    value: 'gridMoodboard',
  })
}

const activeRefinement = (items: CurrentRefinements[], filter?: string) => {
  const length = filter
    ? getActiveRefinementLength(items, filter)
    : getAllActiveRefinementLength(items)

  return length ? `(${length})` : ''
}

const getRadioIcon = (value: PlpGridType) => {
  if (gridsMap[value]) {
    return gridsMap[value].icon.sm
  }
}

// GA Events
const itemsEntity = useGAEntity<{
  product_listSize: number
  shop_category: 'category'
  product_listtype: (typeof gridsMap)[keyof typeof gridsMap]['name']
}>('GA_DL_SETUP_ITEMS')

itemsEntity.value = {
  product_listSize: props.items,
  shop_category: 'category',
  product_listtype: activeIcon.value ?? 'grid432',
}

const { setPlpFunctionalBarOnTop } = useSnackbarPosition()
const plpFunctionalBar = ref(null)
const isOnTop = ref(false)
const { top } = useElementBounding(plpFunctionalBar)
const { isSizeRefinementVisible, isColorRefinementVisible } =
  usePlpFunctionBar()

watch(
  () => top.value,
  newVal => {
    isOnTop.value = Number(newVal) <= 20
  },
  {
    immediate: true,
  }
)

watch(
  () => isOnTop.value,
  newVal => {
    setPlpFunctionalBarOnTop(newVal)
  },
  {
    immediate: true,
  }
)
</script>

<template>
  <div
    ref="plpFunctionalBar"
    class="py-md bg-background-secondary text-text-primary positive-padding flex w-full items-center justify-between"
  >
    
<FormKitLazyProvider config-file="true">
<div class="text-book-6 w-1/3">
      {{ $tc('plp.functionalBar.items', items) }}
    </div>
    <div class="flex w-1/3 items-center justify-center gap-6 lg:gap-8">
      <ClientOnly>
        <template v-for="(gridItem, gridType) in gridsMap" :key="gridType">
          <button
            v-if="gridType !== 'gridMoodboard' || editorialContent?.length"
            :class="{
              'hidden md:block': gridType !== activeIcon,
              'md:block':
                gridType !== 'gridMoodboard' || editorialContent?.length,
            }"
            :aria-hidden="
              (activeBreakpoint === 'sm' || activeBreakpoint === '') &&
              gridType !== activeIcon
            "
            :tabindex="
              (activeBreakpoint === 'sm' || activeBreakpoint === '') &&
              gridType !== activeIcon
                ? -1
                : 0
            "
            :aria-label="`${$ts('accessibility.changeLayoutTo')} ${$ts(`accessibility.plp.gridType.${gridType}`)}`"
            @click="
              () => {
                toggleMobileSelector()
                setGrid(gridType)
              }
            "
          >
            <component
              :is="getIcon(gridItem, activeBreakpoint as Breakpoints)"
              aria-hidden="true"
              class="h-5 w-5"
              :class="{
                'text-icon-primary': gridType === activeIcon,
                'text-icon-deselected': gridType !== activeIcon,
              }"
            />
          </button>
        </template>
      </ClientOnly>
    </div>

    <div
      class="text-book-6 inline-flex w-1/3 items-center justify-end gap-8 whitespace-nowrap font-normal"
    >
      <AisCurrentRefinements
        v-if="isSizeRefinementVisible"
        :transform-items="transformRefinements"
      >
        <template #default="{ items }">
          <button
            aria-haspopup="dialog"
            class="hidden lg:block"
            @click="openFilters('size-filters')"
          >
            {{
              `${$ts('plp.functionalBar.size')} ${activeRefinement(items as CurrentRefinements[], 'size.sizeCode')}`
            }}
          </button>
        </template>
      </AisCurrentRefinements>

      <AisCurrentRefinements
        v-if="isColorRefinementVisible"
        :transform-items="transformRefinements"
      >
        <template #default="{ items }">
          <button
            aria-haspopup="dialog"
            class="hidden lg:block"
            @click="openFilters('color-filters')"
          >
            {{
              `${$ts('plp.functionalBar.color')} ${activeRefinement(items as CurrentRefinements[], 'MacroColorDesc')}`
            }}
          </button>
        </template>
      </AisCurrentRefinements>

      <AisCurrentRefinements :transform-items="transformRefinements">
        <template #default="{ items }">
          <button
            aria-haspopup="dialog"
            class="flex items-center justify-start gap-2"
            @click="openDialog(sidePlpFilters)"
          >
            <span class="hidden md:block">
              {{
                `${$ts('plp.functionalBar.sortAndFilters')} ${activeRefinement(items as CurrentRefinements[])}`
              }}
            </span>
            <DefaultIconsFilters width="20" height="20" />
          </button>
        </template>
      </AisCurrentRefinements>
    </div>

    <Transition
      enter-active-class="transition-opacity ease-out duration-300"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity ease-in duration-200"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="isIconsMobile"
        class="bg-neutral-gray/90 fixed inset-0 z-[999]"
        @click="toggleMobileSelector"
      />
    </Transition>
    <Transition
      enter-active-class="transition-all duration-500 overflow-hidden ease-in"
      enter-from-class="transform max-h-0"
      enter-to-class="transform max-h-full"
      leave-active-class="transition-all duration-500 overflow-hidden ease-out"
      leave-from-class="transform  max-h-full"
      leave-to-class="transform max-h-0"
    >
      <div
        v-if="isIconsMobile"
        class="bg-background-plp text-text-primary fixed bottom-[var(--navbar-height)] left-0 right-0 z-[1000] block w-full md:bottom-0 md:hidden"
      >
        <FormKit
          v-model="activeIcon"
          type="myRadio"
          :options="radioOptions"
          name="mobileGridSelector"
          :classes="{
            wrapper: '$reset w-full flex items-center mb-1 py-1 cursor-pointer',
            options: 'flex flex-col flex-wrap',
            option:
              'border-y first:border-t last:border-b-none border-stroke-inactive px-6 py-4',
          }"
        >
          <template #suffix="context">
            <component
              :is="getRadioIcon(context.option.value ?? 'gridMoodboard')"
              class="ml-1 mr-2 h-5 w-5"
            />
          </template>
        </FormKit>
      </div>
    </Transition>
</FormKitLazyProvider>

  </div>
</template>
